/**
 * DeleteListConfirmModal — confirms list deletion with two branches:
 * "Move X tasks to [dropdown]" or "Delete all X tasks".
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskList, DeleteTaskListAction } from '@/types/task-lists';

type DeleteListConfirmModalProps = {
  visible: boolean;
  list: TaskList | null;
  otherLists: TaskList[]; // candidates for "move to" dropdown
  taskCount: number;
  onConfirm: (action: DeleteTaskListAction) => Promise<void>;
  onDismiss: () => void;
};

export function DeleteListConfirmModal({
  visible,
  list,
  otherLists,
  taskCount,
  onConfirm,
  onDismiss,
}: DeleteListConfirmModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scale = useSharedValue(0.95);
  const opacity = useSharedValue(0);
  const [mode, setMode] = useState<'move' | 'delete'>('move');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isEmpty = taskCount === 0;

  useEffect(() => {
    if (visible) {
      scale.value = withTiming(1, { duration: 150 });
      opacity.value = withTiming(1, { duration: 150 });
      // Empty lists skip the move/delete choice — there's nothing to move.
      setMode(isEmpty ? 'delete' : 'move');
      setTargetId(otherLists[0]?.id ?? null);
      setError(null);
    } else {
      scale.value = withTiming(0.95, { duration: 150 });
      opacity.value = withTiming(0, { duration: 150 });
    }
  }, [visible, otherLists, scale, opacity, isEmpty]);

  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));
  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value * 0.5 }));

  const handleConfirm = useCallback(() => {
    setError(null);
    if (mode === 'move' && !targetId) {
      setError('Select a destination list.');
      return;
    }
    // Parent applies optimistic mutations and closes the modal synchronously;
    // we don't await — the network call runs in the background.
    if (mode === 'move' && targetId) {
      void onConfirm({ action: 'move', targetListId: targetId });
    } else {
      void onConfirm({ action: 'delete' });
    }
  }, [mode, targetId, onConfirm]);

  if (!list) return null;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onDismiss}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
      </Animated.View>
      <View style={styles.centered} pointerEvents="box-none">
        <Animated.View
          style={[styles.card, { backgroundColor: palette.cardBackground }, cardStyle]}
        >
          <Text style={[styles.title, { color: palette.text }]}>Delete &quot;{list.name}&quot;</Text>
          <Text style={[styles.body, { color: palette.textMuted }]}>
            {isEmpty
              ? 'This list is empty. Are you sure you want to delete it?'
              : `This list has ${taskCount} task${taskCount === 1 ? '' : 's'}.`}
          </Text>

          {!isEmpty && (
            <>
              <TouchableOpacity
                onPress={() => setMode('move')}
                activeOpacity={0.7}
                style={styles.radioRow}
              >
                <Ionicons
                  name={mode === 'move' ? 'radio-button-on' : 'radio-button-off'}
                  size={20}
                  color={palette.tint}
                />
                <Text style={[styles.radioLabel, { color: palette.text }]}>
                  Move tasks to another list
                </Text>
              </TouchableOpacity>

              {mode === 'move' && (
                <ScrollView style={styles.dropdown} nestedScrollEnabled>
                  {otherLists.map(l => (
                    <TouchableOpacity
                      key={l.id}
                      onPress={() => setTargetId(l.id)}
                      activeOpacity={0.7}
                      style={[
                        styles.dropdownRow,
                        targetId === l.id && { backgroundColor: palette.borderMuted },
                      ]}
                    >
                      <Text style={[styles.dropdownLabel, { color: palette.text }]}>{l.name}</Text>
                      {targetId === l.id && (
                        <Ionicons name="checkmark" size={18} color={palette.tint} />
                      )}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}

              <TouchableOpacity
                onPress={() => setMode('delete')}
                activeOpacity={0.7}
                style={styles.radioRow}
              >
                <Ionicons
                  name={mode === 'delete' ? 'radio-button-on' : 'radio-button-off'}
                  size={20}
                  color={palette.danger}
                />
                <Text style={[styles.radioLabel, { color: palette.text }]}>
                  Delete all {taskCount} task{taskCount === 1 ? '' : 's'}
                </Text>
              </TouchableOpacity>
            </>
          )}

          {error && <Text style={[styles.error, { color: palette.danger }]}>{error}</Text>}

          <View style={styles.actionRow}>
            <TouchableOpacity onPress={onDismiss} style={styles.buttonGhost}>
              <Text style={[styles.buttonText, { color: palette.textMuted }]}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleConfirm}
              disabled={mode === 'move' && !targetId}
              style={[
                styles.buttonDanger,
                {
                  backgroundColor: palette.danger,
                  opacity: mode === 'move' && !targetId ? 0.5 : 1,
                },
              ]}
            >
              <Text style={[styles.buttonText, { color: '#fff' }]}>Delete</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: '#000' },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: {
    width: 320,
    padding: Spacing.lg,
    borderRadius: BorderRadius.lg,
    gap: Spacing.sm,
  },
  title: { fontSize: 17, fontWeight: '600' },
  body: { fontSize: 14 },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
  },
  radioLabel: { fontSize: 15 },
  dropdown: {
    maxHeight: 180,
    borderRadius: BorderRadius.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(0,0,0,0.1)',
    marginLeft: 28,
  },
  dropdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  dropdownLabel: { fontSize: 15 },
  error: { fontSize: 13 },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  buttonGhost: { paddingHorizontal: Spacing.md, paddingVertical: Spacing.sm },
  buttonDanger: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  buttonText: { fontSize: 15, fontWeight: '600' },
});
